import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Search, 
  Phone, 
  Mail, 
  ShoppingBag,
  Calendar,
  MessageCircle,
  Heart,
  ExternalLink,
  Ticket,
  MapPin,
  User,
  Clock,
  Building2,
  RefreshCw
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Supporthub deep link format (adjust based on actual URL structure)
const SUPPORTHUB_TICKET_URL = "https://hearclear.supporthub.io/ticket";

export default function CustomersPage({ user, logout }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchCustomers();
  }, [searchQuery]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      let url = `${API}/customers?limit=100`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setCustomers(response.data.customers || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const openCustomerDetail = async (customer) => {
    try {
      const response = await axios.get(`${API}/customers/${customer.customer_id}`, { withCredentials: true });
      setSelectedCustomer(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error("Failed to load customer details");
    }
  };

  const openSupporthubTicket = (ticketNo) => {
    // Open Supporthub ticket in new tab
    window.open(`${SUPPORTHUB_TICKET_URL}/${ticketNo}`, '_blank');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="customers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Customers</h1>
            <p className="text-slate-600 mt-1">
              Manage your customer relationships • {total} total customers
            </p>
          </div>
          <Button variant="outline" onClick={fetchCustomers}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, phone, or email..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-customers-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Customers Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Customers</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : customers.length === 0 ? (
              <div className="py-16 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900">No customers yet</h3>
                <p className="text-slate-500 mt-1">Convert leads to customers to see them here</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Ticket #</TableHead>
                    <TableHead>Clinic</TableHead>
                    <TableHead>Appointment</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((customer) => (
                    <TableRow 
                      key={customer.customer_id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => openCustomerDetail(customer)}
                      data-testid={`customer-row-${customer.customer_id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-cyan-700 font-bold">
                              {customer.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{customer.name}</p>
                            {customer.email && (
                              <p className="text-xs text-slate-500">{customer.email}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-600">{customer.phone}</span>
                      </TableCell>
                      <TableCell>
                        {customer.supporthub_ticket_no ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-purple-600 hover:text-purple-700 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              openSupporthubTicket(customer.supporthub_ticket_no);
                            }}
                            data-testid={`ticket-link-${customer.customer_id}`}
                          >
                            <Ticket className="w-4 h-4 mr-1" />
                            {customer.supporthub_ticket_no}
                            <ExternalLink className="w-3 h-3 ml-1" />
                          </Button>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-600">
                          {customer.supporthub_data?.clinic_name || "-"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {customer.supporthub_data?.appointment_datetime ? (
                          <Badge variant="outline" className="text-green-700 border-green-300">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatDateTime(customer.supporthub_data.appointment_datetime)}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-slate-500">
                          {formatDateTime(customer.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`https://wa.me/91${customer.phone}`, '_blank');
                            }}
                          >
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`tel:${customer.phone}`, '_blank');
                            }}
                          >
                            <Phone className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Customer Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedCustomer && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-xl flex items-center gap-2">
                    <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                      <span className="text-cyan-700 font-bold">
                        {selectedCustomer.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    {selectedCustomer.name}
                  </DialogTitle>
                  {selectedCustomer.supporthub_ticket_no && (
                    <DialogDescription className="flex items-center gap-2">
                      <Ticket className="w-4 h-4 text-purple-600" />
                      Supporthub Ticket: 
                      <Button
                        variant="link"
                        className="text-purple-600 p-0 h-auto"
                        onClick={() => openSupporthubTicket(selectedCustomer.supporthub_ticket_no)}
                      >
                        {selectedCustomer.supporthub_ticket_no}
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </DialogDescription>
                  )}
                </DialogHeader>

                <div className="space-y-6 mt-4">
                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500 flex items-center gap-2">
                        <Phone className="w-4 h-4" /> Phone
                      </p>
                      <p className="font-medium mt-1">{selectedCustomer.phone}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500 flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Email
                      </p>
                      <p className="font-medium mt-1">{selectedCustomer.email || "Not provided"}</p>
                    </div>
                  </div>

                  {/* Supporthub Appointment Details */}
                  {selectedCustomer.supporthub_data && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Appointment Details
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <p className="text-xs text-purple-600 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Clinic
                          </p>
                          <p className="font-medium text-purple-900 mt-1">
                            {selectedCustomer.supporthub_data.clinic_name || "-"}
                          </p>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-blue-600 flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Type
                          </p>
                          <p className="font-medium text-blue-900 mt-1">
                            {selectedCustomer.supporthub_data.appointment_type_name || "-"}
                          </p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <User className="w-3 h-3" /> Consultant
                          </p>
                          <p className="font-medium text-green-900 mt-1">
                            {selectedCustomer.supporthub_data.consultant_name || "-"}
                          </p>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-lg">
                          <p className="text-xs text-orange-600 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Appointment
                          </p>
                          <p className="font-medium text-orange-900 mt-1">
                            {formatDateTime(selectedCustomer.supporthub_data.appointment_datetime)}
                          </p>
                        </div>
                      </div>
                      {selectedCustomer.supporthub_data.comment && (
                        <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500">Notes</p>
                          <p className="text-sm text-slate-700 mt-1">
                            {selectedCustomer.supporthub_data.comment}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <Separator />

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-700">{selectedCustomer.loyalty_points || 0}</p>
                      <p className="text-sm text-green-600">Loyalty Points</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-700">{selectedCustomer.purchase_history?.length || 0}</p>
                      <p className="text-sm text-blue-600">Purchases</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-700">{selectedCustomer.communication_log?.length || 0}</p>
                      <p className="text-sm text-purple-600">Interactions</p>
                    </div>
                  </div>

                  {/* Purchase History */}
                  {selectedCustomer.purchase_history?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-2">Purchase History</h4>
                      <div className="space-y-2">
                        {selectedCustomer.purchase_history?.map((purchase, index) => (
                          <div key={index} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <ShoppingBag className="w-4 h-4 text-slate-400" />
                              <span>{purchase.product_name || 'Product'}</span>
                            </div>
                            <span className="font-medium">₹{purchase.amount || 0}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Created Info */}
                  <div className="text-sm text-slate-500 pt-2 border-t">
                    <p>Created: {formatDateTime(selectedCustomer.created_at)}</p>
                    {selectedCustomer.created_by_name && (
                      <p>Created by: {selectedCustomer.created_by_name}</p>
                    )}
                    {selectedCustomer.lead_id && (
                      <p>Original Lead ID: {selectedCustomer.lead_id}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => window.open(`https://wa.me/91${selectedCustomer.phone}`, '_blank')}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                    {selectedCustomer.supporthub_ticket_no && (
                      <Button 
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                        onClick={() => openSupporthubTicket(selectedCustomer.supporthub_ticket_no)}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Open in Supporthub
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
